// Deep-import the concrete module, not the `@naumu/shared` barrel - the barrel
// re-exports every schemas/*.ts and drags zod (~299 KiB) into any chunk that
// touches it, including the marketing shell's.
import { isPreviewableType } from '@naumu/shared/types/attachment';

import type { LightboxAttachment } from '../attachment-lightbox';

import type { StagedAttachment } from './staged-attachment';

/**
 * A staged attachment can be previewed before the message is sent as long as it
 * is a previewable type AND we hold a local object URL for it. The upload may
 * still be in flight — and on the landing composers it never happens at all —
 * so the blob is the only URL that always resolves pre-send.
 */
export function canPreviewStagedAttachment(att: StagedAttachment): boolean {
	return isPreviewableType(att.fileType) && !!att.previewUrl;
}

/**
 * Staged attachments mapped onto the lightbox's shape, in staging order, so the
 * carousel can page between them exactly like a sent message's gallery.
 *
 * `url` is the local object URL, never `/api/attachments/download/{id}`: that
 * endpoint only resolves once the upload lands, and the point of this preview is
 * to work the moment the file is staged.
 */
export function getStagedLightboxAttachments(
	attachments: StagedAttachment[],
): LightboxAttachment[] {
	return attachments.filter(canPreviewStagedAttachment).map((att) => ({
		id: att.id,
		fileName: att.fileName,
		fileType: att.fileType,
		fileSize: att.fileSize,
		url: att.previewUrl as string,
		thumbnailUrl: att.thumbnailUrl,
		width: att.width,
		height: att.height,
		duration: att.duration,
	}));
}
